import { AppRole } from '@/types/auth';
import { LucideIcon } from 'lucide-react';

export interface AppRoute {
  path: string;
  label: string;
  icon: string; // lucide icon name
  group?: 'main' | 'account' | 'admin';
  requiresRole?: AppRole[];
  requiresFeature?: string;
  badge?: string;
  legacy?: string; // old path for redirects
  description?: string;
}

export const APP_ROUTES: AppRoute[] = [
  {
    path: '/app/dashboard',
    label: 'Dashboard',
    icon: 'Home',
    group: 'main',
    legacy: '/dashboard',
    description: 'Your home base for quick actions and insights',
  },
  {
    path: '/app/recipes',
    label: 'Recipes',
    icon: 'ChefHat',
    group: 'main',
    legacy: '/recipes',
    description: 'Manage your recipe collection',
  },
  {
    path: '/app/ingredients',
    label: 'Ingredients & Stock',
    icon: 'Package2',
    group: 'main',
    legacy: '/ingredients',
    description: 'Manage ingredients and stock levels',
  },
  // Inventory merged into Ingredients - redirect handled in App.tsx
  {
    path: '/app/menu-planning',
    label: 'Menu Planning',
    icon: 'Calendar',
    group: 'main',
    legacy: '/menu-planning',
    description: 'Create weekly meal plans',
  },
  {
    path: '/app/menus',
    label: 'My Menus',
    icon: 'CalendarDays',
    group: 'main',
    description: 'View and manage your menu plans',
  },
  {
    path: '/app/orders',
    label: 'My Orders',
    icon: 'ClipboardList',
    group: 'main',
    legacy: '/order-review',
    description: 'View and manage your orders',
  },
  {
    path: '/app/analytics',
    label: 'Analytics',
    icon: 'BarChart3',
    group: 'main',
    legacy: '/analytics',
    requiresFeature: 'analytics',
    description: 'View insights and reports',
  },
  // Sysco Catalog - Hidden from UI, backend preserved for allergen functionality
  // {
  //   path: '/app/sysco-catalog',
  //   label: 'Sysco Catalog',
  //   icon: 'ShoppingCart',
  //   group: 'main',
  //   legacy: '/sysco-catalog',
  //   requiresFeature: 'sysco_integration',
  //   description: 'Browse Sysco products',
  // },
  {
    path: '/app/notifications',
    label: 'Notifications',
    icon: 'Bell',
    group: 'main',
    legacy: '/notifications',
    description: 'View all notifications',
  },
  // Account group
  {
    path: '/app/profile',
    label: 'Profile',
    icon: 'User',
    group: 'account',
    legacy: '/profile',
    description: 'Manage your account settings',
  },
  {
    path: '/app/subscription',
    label: 'Subscription',
    icon: 'CreditCard',
    group: 'account',
    legacy: '/subscription',
    description: 'View billing and plan details',
  },
  {
    path: '/app/support',
    label: 'Support',
    icon: 'LifeBuoy',
    group: 'account',
    legacy: '/support',
    description: 'Get help and submit tickets',
  },
];

// Helper to find route by path
export const getRouteByPath = (path: string): AppRoute | undefined => {
  return APP_ROUTES.find(route => route.path === path);
};

// Helper to get legacy mapping
export const getLegacyRouteMap = (): Map<string, string> => {
  const map = new Map<string, string>();
  APP_ROUTES.forEach(route => {
    if (route.legacy) {
      map.set(route.legacy, route.path);
    }
  });
  return map;
};

// Helper to build breadcrumbs from route
export const buildBreadcrumbs = (currentPath: string) => {
  const breadcrumbs = [{ label: 'Home', path: '/app/dashboard' }];
  const route = getRouteByPath(currentPath);
  
  if (route && route.path !== '/app/dashboard') {
    breadcrumbs.push({ label: route.label, path: route.path });
  }
  
  return breadcrumbs;
};
